import { fromZonedTime, getTimezoneOffset } from 'date-fns-tz';

import { ValueJob } from '@/util/vexo/orderValue';

import { OrderStateV2, JobDraftV2, ShiftDraftV2 } from '../context';

import { CreateOrderPayloadV2, LocationListItem, SurgeRateV2 } from './types';

// Shifts are held in state as naive wall-time strings ("YYYY-MM-DDTHH:MM:SS")
// representing the time at the job's location. The backend stores instants in
// UTC, so we resolve the wall time to UTC at the submission boundary using the
// location's IANA timezone. Without this, the backend interprets the naive
// string as UTC and the displayed shift drifts by the offset (e.g. 6 AM EDT
// rendered as 2 AM EDT).
const MS_PER_HOUR = 60 * 60 * 1000;

// Default timezone: America/New_York (Eastern)
// Matches locationTime.ts for consistency across Order V2
const DEFAULT_TIMEZONE = 'America/New_York';

const resolveTimezone = (tz: string | null | undefined): string =>
  tz || DEFAULT_TIMEZONE;

const toUtcIso = (wallTime: string, timezone: string): string =>
  fromZonedTime(wallTime, timezone).toISOString();

const offsetHoursFor = (wallTime: string, timezone: string): number => {
  const instant = fromZonedTime(wallTime, timezone);
  return getTimezoneOffset(timezone, instant) / MS_PER_HOUR;
};

const surgeRateForDate = (
  surgeRates: SurgeRateV2[],
  shift: ShiftDraftV2,
): SurgeRateV2 | null => {
  // Shifts hold naive wall-time strings ("YYYY-MM-DDTHH:MM:SS"); the date
  // portion already matches the local-calendar date the user selected.
  const dateKey = shift.start_at.slice(0, 10);
  return surgeRates.find((sr) => sr.date === dateKey) || null;
};

// Calculate wall-clock duration in minutes from 24h time strings.
// Ignores timezone and DST to show what was scheduled (e.g., 11 PM to 3 AM = 4 hours).
const wallClockDurationMinutes = (start24h: string, end24h: string): number => {
  const [startH, startM] = start24h.slice(11, 16).split(':').map(Number);
  const [endH, endM] = end24h.slice(11, 16).split(':').map(Number);
  const startMins = startH * 60 + startM;
  const endMins = endH * 60 + endM;

  if (endMins >= startMins) {
    return endMins - startMins;
  }
  // Overnight: end is on next calendar day
  return 24 * 60 - startMins + endMins;
};

// Vexo `order_submitted.total_value` adapter. Reuses the module-private
// wallClockDurationMinutes so the analytics value matches the scheduled
// (DST-agnostic) hours the user saw, and resolves break the same way the
// wire payload does (shift.break_minutes ?? job.mandatory_break_time ?? 0,
// see :181). pay_rate is cents → dollars.
export function buildOrderValueJobsFromState(state: OrderStateV2): ValueJob[] {
  return state.jobs.map((job) => ({
    payRateDollars: (job.pay_rate ?? 0) / 100,
    shifts: (job.shifts ?? []).map((shift) => ({
      grossMinutes: wallClockDurationMinutes(shift.start_at, shift.end_at),
      breakMinutes: shift.break_minutes ?? job.mandatory_break_time ?? 0,
      // Same per-shift worker count the wire payload emits (see :190).
      quantity: shift.quantity ?? job.quantity ?? 1,
    })),
  }));
}

type WireShift = {
  start_at: string;
  end_at: string;
  time_zone_offset: number;
  wall_clock_duration_minutes?: number;
  break_minutes?: number;
  quantity?: number;
};

// Emits one job payload per shift, mirroring legacy `jobStateToJobInput` in
// `CreateGigOrder/context.ts` where each Schedule (always a single calendar
// day in the live UI) produced its own `JobInput`. The legacy `groupId`
// rule is preserved: shifts share `group_id` when `all_shifts_same_worker`
// is true, otherwise each shift gets a sequential `group_id`. Surge-rate
// detection runs per-shift so each emitted job carries the correct
// `is_holiday`/multiplied rates/original rates.
// Backend parity: `JobService.valid_markup_rates?` recomputes the
// expected cost as `(pay.to_f + pay.to_f * markup / 100).round` and
// rejects the job unless `original_cost_rate` equals it exactly. We
// replicate that formula bit-for-bit (integer-cent input, single round)
// rather than reusing `CalculateCost`, whose intermediate 3-decimal
// rounding can land a cent off the backend and still 422.
const markupDerivedCost = (payCents: number, markupPercent: number): number =>
  Math.round(payCents + (payCents * markupPercent) / 100);

const expandJobToPerShiftPayloads = (
  job: JobDraftV2,
  shifts: ShiftDraftV2[],
  tz: string,
  surgeRates: SurgeRateV2[],
  startGroupId: number,
  isMarkup: boolean,
  markupPercent: number,
): { payloads: Record<string, unknown>[]; nextGroupId: number } => {
  const sameWorker = !!job.all_shifts_same_worker;
  // On a markup account the backend validates the *original* pair, so
  // the only safe cost to submit is one re-derived from pay via the
  // backend's own formula. Trusting `job.cost_rate` is exactly what
  // regressed: a rate-quote seed, a surge-multiplied template repeat, or
  // an edit path that moved pay without re-deriving cost all leave
  // `job.cost_rate` off the invariant and the submission fails with
  // "Invalid markup rates". For non-markup accounts the draft's
  // cost_rate (cost-factor derived elsewhere) is kept as-is.
  const baseOriginalPay = job.pay_rate;
  const baseOriginalCost = isMarkup
    ? markupDerivedCost(job.pay_rate, markupPercent)
    : job.cost_rate;
  const visibility =
    job.staffing_strategy === 'OPEN_JOB_POST' ? 'PUBLIC' : 'PRIVATE';
  const publish = visibility === 'PRIVATE' ? job.auto_fill === true : false;
  // V1 parity: hires/invites apply only to schedules the admin had
  // selected on the Staffing screen. The UI mirrors deselections onto
  // `unselected_shift_indexes`; deselected indexes drop the worker
  // assignments on the corresponding emitted job (the shift still gets
  // created, but unstaffed). Undefined / empty means "all shifts
  // selected", which preserves single-shift legacy behavior.
  const unselectedSet = new Set(job.unselected_shift_indexes ?? []);
  // V1 parity: each worker's assignment is scoped to the shift selection
  // captured when the admin hired/invited them (StaffingStep snapshots
  // this onto `worker_shift_scope[workerId]`). Selecting more shifts
  // afterwards must not retroactively pull an existing hire onto the
  // newly selected shift. A missing snapshot means "every selected
  // shift" — preserves template-prefilled / single-shift legacy drafts.
  const workerShiftScope = job.worker_shift_scope ?? {};
  const workerAppliesToShift = (workerId: number, shiftIdx: number) => {
    const scope = workerShiftScope[workerId];
    if (scope == null) return true;
    return scope.includes(shiftIdx);
  };

  const payloads: Record<string, unknown>[] = shifts.map((shift, idx) => {
    const surge = surgeRateForDate(surgeRates, shift);
    const groupId = sameWorker ? startGroupId : startGroupId + idx;
    const wireShift: WireShift = {
      ...shift,
      start_at: toUtcIso(shift.start_at, tz),
      end_at: toUtcIso(shift.end_at, tz),
      time_zone_offset: offsetHoursFor(shift.start_at, tz),
      wall_clock_duration_minutes: wallClockDurationMinutes(
        shift.start_at,
        shift.end_at,
      ),
    };

    const {
      staffing_strategy,
      auto_fill,
      shifts: _omitShifts,
      unselected_shift_indexes: _omitUnselected,
      worker_shift_scope: _omitWorkerScope,
      pay_rate_touched: _omitPayRateTouched,
      invite_workers,
      hire_workers,
      ...rest
    } = job;
    void staffing_strategy;
    void auto_fill;
    void _omitShifts;
    void _omitUnselected;
    void _omitWorkerScope;
    void _omitPayRateTouched;

    // Deselected shifts emit jobs WITHOUT the invite/hire arrays — the
    // shift is still created but no workers are pre-attached to it,
    // matching legacy "hire only on selected schedules". Selected shifts
    // only carry the workers whose captured scope includes this shift.
    const isShiftUnselected = unselectedSet.has(idx);
    const inviteWorkersForShift = isShiftUnselected
      ? []
      : (invite_workers ?? []).filter((w) => workerAppliesToShift(w, idx));
    const hireWorkersForShift = isShiftUnselected
      ? []
      : (hire_workers ?? []).filter((w) => workerAppliesToShift(w, idx));

    // V1 parity: each schedule produced a JobInput with that schedule's
    // own worker count. V2 stored a single `shift.quantity` and spread it
    // through `...rest`, so a draft with shifts {qty 1, qty 3} emitted
    // two jobs both at qty 3. Override per emitted job with the shift's
    // own quantity, falling back to job.quantity (then 1) so legacy
    // single-quantity orders keep behaving exactly as before.
    const perShiftQuantity = shift.quantity ?? job.quantity ?? 1;
    // Same fix for break duration: the backend Job model has a
    // job-level `mandatory_break_time` column (see
    // gravywork-graphy/app/models/job.rb#mandatory_break_time and
    // job_creation_service.rb), and v2 was emitting the job-level
    // `mandatory_break_time` clobbered by `writeShifts` to the last
    // edited break. Override per emitted job with that shift's own
    // `break_minutes` so each shift persists its own break.
    const perShiftBreak = shift.break_minutes ?? job.mandatory_break_time ?? 0;

    if (surge) {
      return {
        ...rest,
        shifts: [wireShift],
        job_type: rest.job_type,
        visibility,
        publish,
        group_id: groupId,
        quantity: perShiftQuantity,
        mandatory_break_time: perShiftBreak,
        invite_workers: inviteWorkersForShift,
        hire_workers: hireWorkersForShift,
        is_holiday: true,
        // Surge multiplies the markup-validated base pair so the
        // displayed/billed rate still surges, while the *original* pair
        // the backend re-validates stays exactly on the markup invariant.
        pay_rate: Math.round(baseOriginalPay * surge.rate),
        cost_rate: Math.round(baseOriginalCost * surge.rate),
        original_pay_rate: baseOriginalPay,
        original_cost_rate: baseOriginalCost,
      };
    }
    // Always derive `original_*` from the current pay/cost on the job
    // rather than `...rest` (which carries `state.original_*`). On a
    // markup billing account, the backend validates
    // `valid_markup_rates?(original_pay_rate, original_cost_rate,
    // markup_percent)` — if those are stale (e.g. a template was loaded
    // onto an account with a different markup, or a rate quote happened
    // before the user edited the pay rate), the invariant
    // `cost = pay + pay * markup / 100` no longer holds against the
    // current markup and the submission fails with "Invalid markup rates".
    // v1's `updSchedulesWithHolidayRates` keeps original_* aligned with
    // calcPayRate/calcBillRate the same way (CreateGigOrder Schedule util).
    return {
      ...rest,
      shifts: [wireShift],
      job_type: rest.job_type,
      visibility,
      publish,
      group_id: groupId,
      quantity: perShiftQuantity,
      mandatory_break_time: perShiftBreak,
      invite_workers: inviteWorkersForShift,
      hire_workers: hireWorkersForShift,
      // Submit the markup-validated pair (pay unchanged; cost re-derived
      // for markup, untouched for non-markup) for both the live and the
      // backend-validated `original_*` fields so they can never disagree.
      pay_rate: baseOriginalPay,
      cost_rate: baseOriginalCost,
      original_pay_rate: baseOriginalPay,
      original_cost_rate: baseOriginalCost,
    };
  });

  const nextGroupId = sameWorker
    ? startGroupId + 1
    : startGroupId + shifts.length;

  return { payloads, nextGroupId };
};

export const buildCreatePayload = (
  state: OrderStateV2,
  locations: LocationListItem[] = [],
  surgeRates: SurgeRateV2[] = [],
): CreateOrderPayloadV2 => {
  const jobs: Record<string, unknown>[] = [];
  let groupCounter = 1;
  const isMarkup = state.billingAccount?.rate_type === 'MARKUP';
  const markupPercent = state.billingAccount?.markup_percent ?? 0;

  // Emit jobs in `state.jobs` order, which is already entry order at
  // submit time: `addNewJobDraft` prepends so the wizard (which always
  // reads/writes `jobs[0]`) edits the latest draft, but
  // `finishNewJobDraftReducer` rotates that fresh draft to the end of
  // the array when the user advances back into the Review step. By the
  // time we serialize, `state.jobs[0]` is the FIRST-added skill and the
  // last entry is the most recently added one — exactly the order the
  // user added them. The persisted payload (and therefore the repeat
  // template's title + replay order) needs to match, so "Coat Check
  // then Cook then Chef" stays in that order when the user reuses the
  // template or repeats the order. An earlier version reversed here on
  // the (stale) assumption that `state.jobs` was reverse-entry-order;
  // with the rotation reducer in place, reversing flipped the persisted
  // order and templates surfaced skills in last-entered-first order.
  state.jobs.forEach((job) => {
    const location = locations.find((loc) => loc.id === job.address_id);
    const tz = resolveTimezone(location?.timezone);
    const shifts = job.shifts || [];
    // v1 parity: legacy emitted no JobInput for a Schedule-less job draft.
    // Mirror that here — a draft with no shifts contributes no payload.
    if (shifts.length === 0) {
      return;
    }
    const normalizedJob: JobDraftV2 = {
      ...job,
      job_type: state.orderType || job.job_type,
    };
    const { payloads, nextGroupId } = expandJobToPerShiftPayloads(
      normalizedJob,
      shifts,
      tz,
      surgeRates,
      groupCounter,
      isMarkup,
      markupPercent,
    );
    payloads.forEach((p) => jobs.push(p));
    groupCounter = nextGroupId;
  });

  return {
    account_id: state.billingAccount?.id || 0,
    order_type: (state.orderType || 'GIG') as 'GIG' | 'LTA',
    verification_enabled: !!state.verificationEnabled,
    jobs: jobs as CreateOrderPayloadV2['jobs'],
  };
};
